import { z } from 'zod';
import Decimal from 'decimal.js';

// src/address.ts
var addressSchema = z.object({
  fullAddress: z.string().nullable(),
  streetAddress1: z.string().nullable(),
  streetAddress2: z.string().nullable(),
  locality: z.string().nullable(),
  state: z.string().nullable(),
  postcode: z.string().nullable(),
  geoLat: z.union([z.number(), z.instanceof(Decimal)]).nullable(),
  geoLng: z.union([z.number(), z.instanceof(Decimal)]).nullable(),
  country: z.string().nullable(),
  label: z.string().nullable()
});
var addressUpdateSchema = addressSchema.omit({ fullAddress: true }).partial();
var date = z.string().pipe(z.coerce.date());
var createdOn = date;
var id = z.string().uuid();

// src/customer.ts
var address = z.object({
  fullAddress: z.string(),
  isPrimary: z.boolean()
});
var funding = z.object({
  NDISNumber: z.string()
});
var status = z.enum(["ACTIVE", "ENQUIRY", "ARCHIVED", "SCHEDULED_TO_BE_ARCHIVED"]);
var customer = z.object({
  addresses: z.array(address),
  attachmentUrl: z.string().optional(),
  createdOn,
  customerStatus: status,
  displayName: z.string(),
  funding,
  userId: id,
  pinnedAlertNumber: z.number(),
  dateOfBirth: z.string().nullish()
});
var category = z.enum(["workflow", "booking", "planManagement", "session", "payment"]);
var status2 = z.enum(["read", "unread", "unseen"]);
var notification = z.object({
  category,
  createdOn,
  id,
  status: status2,
  summary: z.string(),
  description: z.string(),
  meta: z.record(z.unknown())
});
var categoryType = z.object({
  name: category,
  title: z.string()
});
z.object({
  total_count: z.number().min(0),
  notifications: z.array(notification)
});
z.object({
  categories: z.array(categoryType)
});
z.object({
  category: z.array(z.string()),
  page: z.string(),
  status: z.string()
});
var serviceAgreementDetails = z.object({
  userServiceAgreementServiceId: z.string(),
  startDate: z.union([z.string().datetime({ offset: true }), z.date()]).pipe(z.coerce.date()),
  endDate: z.union([z.string().datetime({ offset: true }), z.date()]).pipe(z.coerce.date()),
  serviceName: z.string()
});
var userDetails = z.object({
  userId: z.string(),
  displayName: z.string(),
  NDISNumber: z.string(),
  fullAddress: z.string(),
  attachmentUrl: z.string()
});
var SILServiceDetailsResponse = z.object({
  serviceAgreementDetails,
  userDetails,
  serviceProviderId: z.string()
});
var literalSchema = z.union([z.string(), z.number(), z.boolean()]);
var jsonSchema = z.lazy(() => z.union([literalSchema, z.array(jsonSchema), z.record(jsonSchema)]));
var serviceLocationUpdateSchema = z.object({
  serviceLocationId: z.string(),
  mmmGroup: z.number().nullable().optional(),
  locationType: z.string().nullable().optional(),
  ...addressUpdateSchema.shape
});
var serviceAttachmentsUpdateSchema = z.object({
  serviceAttachmentId: z.string().optional(),
  attachmentUrl: z.string().nullish().optional(),
  attachmentPath: z.string().nullish().optional(),
  attachmentType: z.string().nullish().optional()
});
var serviceUpdateSchema = z.object({
  serviceName: z.string().optional(),
  serviceDescription: z.string().optional(),
  bookingPeriodType: z.string().nullish().optional(),
  bookingTimeType: z.string().optional(),
  minBookingMinutes: z.number().int().nullish().optional(),
  gapMinutes: z.number().int().nullish().optional(),
  maxBookingMinutes: z.number().int().nullish().optional(),
  recurringType: z.string().optional(),
  serviceType: z.string().optional(),
  serviceDirection: z.string().optional(),
  workerSelectionType: z.string().optional(),
  priceFrequency: z.string().nullish().optional(),
  price: z.number().nullish().optional(),
  currency: z.string().optional(),
  paymentType: z.string().nullish().optional(),
  capacity: z.number().int().nullish().optional(),
  servicePublishDate: z.date().nullish().optional(),
  status: z.string().optional(),
  createdBy: z.string().nullish().optional(),
  likeCount: z.number().int().optional(),
  dislikeCount: z.number().int().optional(),
  createdOn: z.date().optional(),
  updatedOn: z.date().nullish().optional(),
  availableFrom: z.date().nullish().optional(),
  availableTo: z.date().nullish().optional(),
  serviceConfig: jsonSchema.optional(),
  cancellationShortPeriod: z.number().int().optional(),
  serviceStockNumber: z.string().nullish().optional(),
  isPublicMarketplace: z.boolean().optional(),
  timezone: z.string().optional(),
  serviceDepartmentId: z.string().optional(),
  isUseWorkerTimeForAutoBilling: z.boolean().optional(),
  workerTimeLeeway: z.number().int().optional(),
  serviceClaimConfig: jsonSchema.optional(),
  bookingTimeWarningSource: z.string().optional(),
  canWorkerEditCheckInCheckOutTime: z.boolean().optional(),
  serviceCategory: z.string().nullish().optional(),
  groupServiceClassification: z.string().nullish().optional(),
  isFixedCapacity: z.boolean().optional(),
  teamMemberCustomerRatio: jsonSchema.optional(),
  siteCode: z.string().nullish().optional(),
  siteName: z.string().nullish().optional(),
  defaultNotePrivacyVisible: z.string().optional(),
  defaultActivityRecordIncrement: z.number().int().optional(),
  locations: z.array(serviceLocationUpdateSchema).optional(),
  attachments: z.array(serviceAttachmentsUpdateSchema).optional(),
  rosterStartDate: z.date().nullish().optional(),
  rosterPeriod: z.string().nullish().optional()
});

// src/permissions/user-permissions.ts
var UserPermissionRole = /* @__PURE__ */ ((UserPermissionRole2) => {
  UserPermissionRole2[UserPermissionRole2["NoAccess"] = 0] = "NoAccess";
  UserPermissionRole2[UserPermissionRole2["PrimaryOwner"] = 1] = "PrimaryOwner";
  UserPermissionRole2[UserPermissionRole2["Owner"] = 2] = "Owner";
  UserPermissionRole2[UserPermissionRole2["CustomerAccessManager"] = 3] = "CustomerAccessManager";
  UserPermissionRole2[UserPermissionRole2["CustomerAccessViewOnly"] = 4] = "CustomerAccessViewOnly";
  UserPermissionRole2[UserPermissionRole2["TeamManager"] = 5] = "TeamManager";
  UserPermissionRole2[UserPermissionRole2["TeamViewOnly"] = 6] = "TeamViewOnly";
  UserPermissionRole2[UserPermissionRole2["PaymentManager"] = 7] = "PaymentManager";
  UserPermissionRole2[UserPermissionRole2["PaymentMember"] = 8] = "PaymentMember";
  UserPermissionRole2[UserPermissionRole2["ServiceAdmin"] = 9] = "ServiceAdmin";
  UserPermissionRole2[UserPermissionRole2["ServiceMember"] = 10] = "ServiceMember";
  UserPermissionRole2[UserPermissionRole2["PlanManager"] = 11] = "PlanManager";
  UserPermissionRole2[UserPermissionRole2["DepartmentManager"] = 12] = "DepartmentManager";
  UserPermissionRole2[UserPermissionRole2["WorkflowBasic"] = 13] = "WorkflowBasic";
  UserPermissionRole2[UserPermissionRole2["WorkflowElevated"] = 14] = "WorkflowElevated";
  UserPermissionRole2[UserPermissionRole2["WorkflowManager"] = 15] = "WorkflowManager";
  UserPermissionRole2[UserPermissionRole2["FormBuilder"] = 16] = "FormBuilder";
  UserPermissionRole2[UserPermissionRole2["SubscriptionBillingAllAccess"] = 17] = "SubscriptionBillingAllAccess";
  UserPermissionRole2[UserPermissionRole2["SubscriptionBillingViewOnly"] = 18] = "SubscriptionBillingViewOnly";
  UserPermissionRole2[UserPermissionRole2["CustomerViewAll"] = 19] = "CustomerViewAll";
  UserPermissionRole2[UserPermissionRole2["CustomerViewServiceOnly"] = 20] = "CustomerViewServiceOnly";
  UserPermissionRole2[UserPermissionRole2["TeamNoAccess"] = 21] = "TeamNoAccess";
  return UserPermissionRole2;
})(UserPermissionRole || {});
var FineGrainedAction = /* @__PURE__ */ ((FineGrainedAction3) => {
  FineGrainedAction3["CustomerContactsAccess"] = "CustomerContactsAccess";
  FineGrainedAction3["CustomerFundingAccess"] = "CustomerFundingAccess";
  FineGrainedAction3["CustomerServiceAgreementsAccess"] = "CustomerServiceAgreementsAccess";
  FineGrainedAction3["CustomerBudgetsAccess"] = "CustomerBudgetsAccess";
  return FineGrainedAction3;
})(FineGrainedAction || {});
var {
  Owner,
  PrimaryOwner,
  TeamManager,
  ServiceAdmin,
  CustomerAccessManager,
  CustomerAccessViewOnly,
  PaymentManager,
  PaymentMember,
  ServiceMember,
  TeamViewOnly,
  TeamNoAccess,
  PlanManager,
  WorkflowBasic,
  WorkflowElevated,
  WorkflowManager,
  FormBuilder,
  SubscriptionBillingViewOnly,
  SubscriptionBillingAllAccess
} = UserPermissionRole;
function createPermission(permissionRole, serviceDepartmentId, serviceId) {
  return { permissionRole, serviceDepartmentId, serviceId };
}
var customerViewOnlyPerm = createPermission(CustomerAccessViewOnly, null, null);
var teamViewOnlyPerm = createPermission(TeamViewOnly, null, null);
var defaultPermissions = {
  hasPortalAccess: true,
  hasWorkerAppAccess: false,
  permissionRoles: [customerViewOnlyPerm, teamViewOnlyPerm]
};
var userPermission = {
  ViewTeamListing: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
    requireValidateServiceAccess: false
  },
  ViewTeamLiteListing: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager, TeamViewOnly, ServiceAdmin],
    requireValidateServiceAccess: false
  },
  ViewBookingListing: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  EditServiceDepartment: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  ViewServiceDepartment: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  ArchiveServiceDepartment: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  ViewBookingDetail: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  WaivePaymentBillingLineItem: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  UnwaivePaymentBillingLineItem: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  ApproveBooking: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  WaiveBooking: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  ResendInvoice: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  RejectPaymentBillingLineItem: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  ReApproveBillingLineItem: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  EditPaymentBillingLineItem: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  DeletePaymentBillingLineItem: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager],
    requireValidateServiceAccess: false
  },
  ProcessPaymentBillingLineItem: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager],
    requireValidateServiceAccess: false
  },
  CreateBookingBillingLineItem: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  DownloadNDISFile: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  DownloadFinanceFile: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  ViewPayments: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  ViewInvoices: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  EditBookingCaseNote: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  DeleteBookingCaseNote: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember, CustomerAccessManager],
    requireValidateServiceAccess: true
  },
  CreateBooking: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  EditWorkerInBooking: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  EditBookingLocation: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  EditBookingBillingLineItems: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  CancelBooking: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  EditBookingTime: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  AddBookingDocument: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  EditBookingDocument: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  DeleteBookingDocument: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  AcceptBooking: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  StartBooking: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  FinishBooking: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  CompleteBooking: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  ConfirmBooking: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  DeclineBooking: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  ViewServiceDepartmentListing: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  ViewServiceDepartmentLiteListing: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, TeamManager],
    requireValidateServiceAccess: false
  },
  ChangePrimaryOwner: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  EditServiceDetail: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  CreateService: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  PublishService: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  UnpublishService: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  ViewServiceListing: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  ViewServiceLiteListing: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  ViewServiceDetail: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  EditCustomerProfile: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  AddNewTeamMember: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  ViewCustomerProfile: {
    permissionRoles: [
      PrimaryOwner,
      Owner,
      CustomerAccessManager,
      CustomerAccessViewOnly,
      ServiceAdmin,
      ServiceMember,
      PlanManager
    ],
    requireValidateServiceAccess: false
  },
  ViewCustomerListing: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly],
    requireValidateServiceAccess: false
  },
  EditTeamMemberProfile: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  EditUserTimezone: {
    permissionRoles: [
      PrimaryOwner,
      Owner,
      CustomerAccessManager,
      CustomerAccessViewOnly,
      ServiceAdmin,
      ServiceMember,
      PlanManager
    ],
    requireValidateServiceAccess: false
  },
  ViewTeamManagement: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  ViewDebtors: {
    permissionRoles: [PrimaryOwner, Owner, PaymentMember, PaymentManager, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  ViewDebtorDetails: {
    permissionRoles: [PrimaryOwner, Owner, PaymentMember, PaymentManager],
    requireValidateServiceAccess: false
  },
  EditDebtor: {
    permissionRoles: [PrimaryOwner, Owner, PaymentMember, PaymentManager, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  AddDebtor: {
    permissionRoles: [PrimaryOwner, Owner, PaymentMember, PaymentManager, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  ArchiveDebtor: {
    permissionRoles: [PrimaryOwner, Owner, PaymentMember, PaymentManager, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  UnarchiveDebtor: {
    permissionRoles: [PrimaryOwner, Owner, PaymentMember, PaymentManager, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  RemoveCustomerDebtor: {
    permissionRoles: [PrimaryOwner, Owner, PaymentMember, PaymentManager, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  ViewDebtorCustomersList: {
    permissionRoles: [PrimaryOwner, Owner, PaymentMember, PaymentManager],
    requireValidateServiceAccess: false
  },
  ViewDebtorInvoicesList: {
    permissionRoles: [PrimaryOwner, Owner, PaymentMember, PaymentManager],
    requireValidateServiceAccess: false
  },
  ViewTeamMemberProfile: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
    requireValidateServiceAccess: false
  },
  EditCompanyDetail: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  ViewCompanyDetail: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  ViewServiceMessagesListing: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  ViewServiceMessages: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  DeleteDraftTeamMember: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  RemoveTeamMember: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  CancelTeamMemberRemoval: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  BlockTeamMember: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  InviteDraftTeamMember: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  ResendInvites: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  RevokeInvites: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  UnblockTeamMember: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  UpdateInvites: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  EditTeamMemberPermissions: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  ViewTeamMemberPermissions: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  ViewCustomerLiteListing: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  ViewServiceProviderDetailLite: {
    permissionRoles: [
      Owner,
      PrimaryOwner,
      TeamManager,
      ServiceAdmin,
      CustomerAccessManager,
      CustomerAccessViewOnly,
      PaymentManager,
      PaymentMember,
      ServiceMember,
      TeamViewOnly,
      FormBuilder,
      PlanManager
    ],
    requireValidateServiceAccess: false
  },
  ChangeBookingBillingStatus: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  ViewRecurringBookingsByStatus: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  ArchiveService: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  EditFundingAndPayment: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  ViewFundingAndPayment: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  CreateCustomerServiceAgreement: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  UpdateCustomerServiceAgreement: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  DeleteCustomerServiceAgreement: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  CreateCustomerNote: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  EditCustomerNote: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  DeleteCustomerNote: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  CreatePlanManagementSupplier: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  ViewPlanManagementInvoices: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  ViewPlanManagementProviders: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  UpdatePlanManagementInvoices: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  ViewPlanManagementCustomers: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  ViewPlanManagementSupplierDetails: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  UpdatePlanManagementSupplierDetails: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  UpdatePlanManagementInvoiceWaive: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  ProcessPlanManagementInvoice: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  RejectPlanManagementInvoice: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  PayPlanManagementItem: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  FailedPlanManagementInvoice: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  UnpaidPlanManagementInvoice: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  ViewInvoiceBookingList: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  ViewLineItems: {
    permissionRoles: [
      Owner,
      PrimaryOwner,
      TeamManager,
      ServiceAdmin,
      CustomerAccessManager,
      CustomerAccessViewOnly,
      PaymentManager,
      PaymentMember,
      ServiceMember,
      TeamViewOnly,
      PlanManager
    ],
    requireValidateServiceAccess: false
  },
  EditTermsConditionsTemplate: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  ViewServiceProviderPaymentSources: {
    permissionRoles: [
      Owner,
      PrimaryOwner,
      TeamManager,
      ServiceAdmin,
      CustomerAccessManager,
      PaymentManager,
      PaymentMember,
      ServiceMember,
      PlanManager
    ],
    requireValidateServiceAccess: false
  },
  ExportPlatformData: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  ImportPlatformData: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  ArchiveBooking: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  AddCustomerToGroupServiceSessions: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  ViewGroupService: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  EditGroupServiceStatus: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  EditGroupServiceDetail: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  CreateSessions: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  EditSession: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  ViewSessions: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  ApproveCustomerBookings: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  AddShiftSlots: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  ConfirmShiftSlots: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  ViewGroupBookingDetail: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  UpdateGroupBookingScheduledStartEndTime: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  ReinstateGroupBookings: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  ChangeGroupBookingsToCustomerCancel: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  UnchargeGroupBookingCancellationFee: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  CloseSession: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  CreateGroupBookingInstruction: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  DeleteShiftSlots: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  EditShiftSlots: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  EditCustomerBookings: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  EditTimesheet: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  ApproveTimesheet: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  UpdatePendingInvitation: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  CreateCustomerView: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly],
    requireValidateServiceAccess: false
  },
  UpdateCustomerView: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly],
    requireValidateServiceAccess: false
  },
  DuplicateCustomerView: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly],
    requireValidateServiceAccess: false
  },
  ViewCustomerViewListing: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly],
    requireValidateServiceAccess: false
  },
  DeleteCustomerView: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly],
    requireValidateServiceAccess: false
  },
  CreateBookingView: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  UpdateBookingView: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  DuplicateBookingView: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  ViewBookingViewListing: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  DeleteBookingView: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  CreateTeamView: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
    requireValidateServiceAccess: false
  },
  UpdateTeamView: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
    requireValidateServiceAccess: false
  },
  DuplicateTeamView: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
    requireValidateServiceAccess: false
  },
  ViewTeamViewListing: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
    requireValidateServiceAccess: false
  },
  DeleteTeamView: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
    requireValidateServiceAccess: false
  },
  MarkInovicesAsPaid: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  MarkInovicesAsUnpaid: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  UpdateServiceProviderNDIADebtorInfo: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  ViewServiceProviderNDIADebtorInfo: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  ViewCustomerContactListing: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly],
    requireValidateServiceAccess: false
  },
  UpdateForm: {
    permissionRoles: [PrimaryOwner, Owner, FormBuilder],
    requireValidateServiceAccess: false
  },
  ViewForm: {
    permissionRoles: [PrimaryOwner, Owner, FormBuilder],
    requireValidateServiceAccess: false
  },
  SelectForm: {
    permissionRoles: [PrimaryOwner, Owner, FormBuilder, CustomerAccessManager, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  ViewWorkflows: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  CreateBookingForm: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  DeleteBookingForm: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  ViewBookingForm: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  UpdateBookingFormData: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  UpdateGroupSessionFormData: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  ViewGroupSessionForm: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  ViewCustomerForm: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  CreateCustomerForm: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  UpdateCustomerFormData: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  UpdateIntegrations: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  CreateXeroContact: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  AttachXeroContact: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  DeleteCustomerForm: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  DeleteSupportWorkerForm: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  UpdateSupportWorkerFormData: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  CreateSupportWorkerForm: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  ViewSupportWorkerForm: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
    requireValidateServiceAccess: false
  },
  CreateWorkflowTemplate: {
    permissionRoles: [WorkflowManager, PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  UpdateWorkflowTemplateConfigurations: {
    permissionRoles: [WorkflowManager, PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  ViewWorkflowTemplateList: {
    permissionRoles: [
      WorkflowManager,
      PrimaryOwner,
      Owner,
      CustomerAccessManager,
      ServiceAdmin,
      ServiceMember,
      WorkflowElevated
    ],
    requireValidateServiceAccess: false
  },
  ViewWorkflowTemplateInfo: {
    permissionRoles: [WorkflowManager, PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  ViewXeroContacts: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly],
    requireValidateServiceAccess: false
  },
  UpdateSleepoverTimeSlot: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  CreateDisturbance: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: true
  },
  CheckExistWorkflowTemplateRequest: {
    permissionRoles: [WorkflowManager, PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  UpdateWorkflowTemplateSteps: {
    permissionRoles: [WorkflowManager, PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  CreateWorkflowFromBookingNote: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember, WorkflowManager],
    requireValidateServiceAccess: false
  },
  CreateWorkflowFromCustomerNote: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewWorkflowFromBookingNote: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewWorkflowFromCustomerProfile: {
    permissionRoles: [
      PrimaryOwner,
      Owner,
      CustomerAccessManager,
      CustomerAccessViewOnly,
      ServiceAdmin,
      ServiceMember,
      WorkflowManager
    ],
    requireValidateServiceAccess: false
  },
  CreateWorkflowComment: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewWorkflowComment: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  UpdateWorkflowComment: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  DeleteWorkflowComment: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewWorkflowActivityLog: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewListWorkflow: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewListWorkflowAttachment: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  UpdateWorkflowAttachment: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  CreateWorkflowAttachment: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  DeleteWorkflowAttachment: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  CreateWorkflowMembers: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewWorkflowDetail: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewWorkflowOnProfile: {
    permissionRoles: [
      PrimaryOwner,
      Owner,
      CustomerAccessManager,
      TeamManager,
      WorkflowManager,
      CustomerAccessViewOnly,
      TeamViewOnly,
      ServiceAdmin,
      ServiceMember
    ],
    requireValidateServiceAccess: false
  },
  CreateManuallyWorkflow: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowManager, WorkflowElevated],
    requireValidateServiceAccess: false
  },
  ViewTeamMemberInvolved: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowManager, WorkflowElevated, WorkflowBasic],
    requireValidateServiceAccess: false
  },
  WorkflowFlagIssue: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  WorkflowResolve: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  WorkflowApprove: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  WorkflowSendBack: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  WorkflowUndo: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  WorkflowClose: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  WorkflowReopen: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  CreateWorkflowApprovers: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewWorkflowTemplateActiveWorkflows: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewWorkflowLinkedForm: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewWorkflowFormDetail: {
    permissionRoles: [
      PrimaryOwner,
      Owner,
      ServiceAdmin,
      ServiceMember,
      CustomerAccessManager,
      WorkflowBasic,
      WorkflowElevated,
      WorkflowManager
    ],
    requireValidateServiceAccess: false
  },
  UpdateWorkflowFormDetail: {
    permissionRoles: [
      PrimaryOwner,
      Owner,
      ServiceAdmin,
      ServiceMember,
      CustomerAccessManager,
      WorkflowBasic,
      WorkflowElevated,
      WorkflowManager
    ],
    requireValidateServiceAccess: false
  },
  AddWorkflowForm: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  DeleteWorkflowForm: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewWorkflowFormVersion: {
    permissionRoles: [
      PrimaryOwner,
      Owner,
      FormBuilder,
      ServiceAdmin,
      ServiceMember,
      CustomerAccessManager,
      WorkflowBasic,
      WorkflowElevated,
      WorkflowManager
    ],
    requireValidateServiceAccess: false
  },
  AddDocumentForm: {
    permissionRoles: [
      PrimaryOwner,
      Owner,
      FormBuilder,
      ServiceAdmin,
      ServiceMember,
      CustomerAccessManager,
      WorkflowBasic,
      WorkflowElevated,
      WorkflowManager
    ],
    requireValidateServiceAccess: false
  },
  ViewFormLiteList: {
    permissionRoles: [PrimaryOwner, Owner, FormBuilder, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewWorkflowFormPdf: {
    permissionRoles: [PrimaryOwner, Owner, FormBuilder, WorkflowBasic, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewShiftBreakdown: {
    permissionRoles: [PrimaryOwner, Owner, PaymentMember, PaymentManager],
    requireValidateServiceAccess: false
  },
  AssignCustomerSupportCoordinator: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager, CustomerAccessViewOnly],
    requireValidateServiceAccess: false
  },
  AssignSupportCoordinatorToCustomer: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager, TeamViewOnly, TeamNoAccess],
    requireValidateServiceAccess: false
  },
  RemoveCustomerSupportCoordinator: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  PreviewBrandingLogoPdf: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  ViewBrandingDetail: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  ViewFinanceConfiguration: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  UpdateFinanceAccount: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  UpdateFinancePayroll: {
    permissionRoles: [
      PrimaryOwner,
      Owner,
      PaymentManager,
      PaymentMember,
      ServiceAdmin,
      ServiceMember,
      CustomerAccessManager
    ],
    requireValidateServiceAccess: false
  },
  XeroCheckConnection: {
    permissionRoles: [
      PrimaryOwner,
      Owner,
      CustomerAccessManager,
      CustomerAccessViewOnly,
      PaymentManager,
      PaymentMember
    ],
    requireValidateServiceAccess: false
  },
  ViewPlanManagementServiceAgreements: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly],
    requireValidateServiceAccess: false
  },
  ViewDetailPlanManagementServiceAgreement: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager, CustomerAccessViewOnly],
    requireValidateServiceAccess: false
  },
  DeletePlanManagementServiceAgreement: {
    permissionRoles: [PrimaryOwner, Owner, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  CreatePlanManagementServiceAgreement: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  UpdatePlanManagementServiceAgreement: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  ViewSubscriptionStatus: {
    permissionRoles: [
      PrimaryOwner,
      Owner,
      TeamManager,
      FormBuilder,
      SubscriptionBillingAllAccess,
      SubscriptionBillingViewOnly
    ],
    requireValidateServiceAccess: false
  },
  ViewSubscriptionOverview: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess, SubscriptionBillingViewOnly],
    childPermissionRoles: {
      [SubscriptionBillingAllAccess]: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
      [SubscriptionBillingViewOnly]: [Owner, TeamManager, TeamViewOnly]
    },
    requireValidateServiceAccess: false
  },
  UpdateDefaultPaymentMethod: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess],
    childPermissionRoles: {
      [SubscriptionBillingAllAccess]: [PrimaryOwner, Owner, TeamManager, TeamViewOnly]
    },
    requireValidateServiceAccess: false
  },
  ViewListPaymentMethod: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess, SubscriptionBillingViewOnly],
    childPermissionRoles: {
      [SubscriptionBillingAllAccess]: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
      [SubscriptionBillingViewOnly]: [Owner, TeamManager, TeamViewOnly]
    },
    requireValidateServiceAccess: false
  },
  CreateCustomerSetupIntent: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess],
    childPermissionRoles: {
      [SubscriptionBillingAllAccess]: [PrimaryOwner, Owner, TeamManager, TeamViewOnly]
    },
    requireValidateServiceAccess: false
  },
  DeletePaymentMethod: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess],
    childPermissionRoles: {
      [SubscriptionBillingAllAccess]: [PrimaryOwner, Owner, TeamManager, TeamViewOnly]
    },
    requireValidateServiceAccess: false
  },
  UpdatePaymentMethod: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess],
    childPermissionRoles: {
      [SubscriptionBillingAllAccess]: [PrimaryOwner, Owner, TeamManager, TeamViewOnly]
    },
    requireValidateServiceAccess: false
  },
  ViewBillingAccess: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess, SubscriptionBillingViewOnly],
    childPermissionRoles: {
      [SubscriptionBillingAllAccess]: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
      [SubscriptionBillingViewOnly]: [Owner, TeamManager, TeamViewOnly]
    },
    requireValidateServiceAccess: false
  },
  CreateBillingAccess: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess],
    childPermissionRoles: {
      [SubscriptionBillingAllAccess]: [PrimaryOwner]
    },
    requireValidateServiceAccess: false
  },
  RemoveBillingAccess: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess],
    childPermissionRoles: {
      [SubscriptionBillingAllAccess]: [PrimaryOwner]
    },
    requireValidateServiceAccess: false
  },
  ViewListInvoice: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess, SubscriptionBillingViewOnly],
    childPermissionRoles: {
      [SubscriptionBillingAllAccess]: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
      [SubscriptionBillingViewOnly]: [Owner, TeamManager, TeamViewOnly]
    },
    requireValidateServiceAccess: false
  },
  DownloadIndividualInvoice: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess, SubscriptionBillingViewOnly],
    childPermissionRoles: {
      [SubscriptionBillingAllAccess]: [PrimaryOwner, Owner, TeamManager, TeamViewOnly],
      [SubscriptionBillingViewOnly]: [Owner, TeamManager, TeamViewOnly]
    },
    requireValidateServiceAccess: false
  },
  ResumeSubscription: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  PauseSubscription: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  ViewConfirmPauseSubscription: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  UpdateWorkType: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  CreateWorkType: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  ArchiveWorkType: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  RestoreWorkType: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  UpdatePayPeriod: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  // from the frontend app
  ViewGroupSessions: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin, ServiceMember],
    requireValidateServiceAccess: false
  },
  MarkInvoicesAsPaid: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  MarkInvoicesAsUnPaid: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  EditRemovalDate: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  CancelRemovalDate: {
    permissionRoles: [PrimaryOwner, Owner, TeamManager],
    requireValidateServiceAccess: false
  },
  ViewPlanManagement: {
    permissionRoles: [PrimaryOwner, Owner, PlanManager],
    requireValidateServiceAccess: false
  },
  ApproveCancelledBookingTeamMemberPay: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  EditCancelledBookingTeamMemberPayHours: {
    permissionRoles: [PrimaryOwner, Owner, ServiceAdmin],
    requireValidateServiceAccess: true
  },
  ViewDebtorManagement: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember],
    requireValidateServiceAccess: false
  },
  EditCustomerDebtor: {
    permissionRoles: [PrimaryOwner, Owner, PaymentManager, PaymentMember, CustomerAccessManager],
    requireValidateServiceAccess: false
  },
  ViewFinanceFileExportConfiguration: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  SetDefaultFinanceFileExportConfiguration: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  AccessToFormBuilder: {
    permissionRoles: [PrimaryOwner, Owner, FormBuilder],
    requireValidateServiceAccess: false
  },
  AddManualTrigger: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowElevated, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewAddEditWorkflow: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowManager],
    requireValidateServiceAccess: false
  },
  ViewLinkedFormWorkflow: {
    permissionRoles: [PrimaryOwner, Owner, WorkflowManager, WorkflowElevated],
    requireValidateServiceAccess: false
  },
  ViewSubscriptionBilling: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess, SubscriptionBillingViewOnly],
    requireValidateServiceAccess: false
  },
  UpdateSubscriptionCard: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess],
    requireValidateServiceAccess: false
  },
  AccessRetryPaymentLink: {
    permissionRoles: [PrimaryOwner, SubscriptionBillingAllAccess],
    requireValidateServiceAccess: false
  },
  CancelSubscriptionAccess: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  AddBillingAccess: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  ResumeSubscriptionAccess: {
    permissionRoles: [PrimaryOwner],
    requireValidateServiceAccess: false
  },
  ViewEditIntegrations: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  ViewBrandingPage: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  },
  // this isn't used by apps but used by the permissions validation rules to skip checks for owner roles.
  BypassFineGrainedActions: {
    permissionRoles: [PrimaryOwner, Owner],
    requireValidateServiceAccess: false
  }
};

// src/permissions/default-fine-grained-permissions.ts
var ROLES_WITH_CUSTOMER_PROFILE_ACCESS = userPermission.ViewCustomerProfile.permissionRoles;
var DEFAULT_FINE_GRAINED_ACTIONS = {
  ["CustomerContactsAccess" /* CustomerContactsAccess */]: false,
  ["CustomerFundingAccess" /* CustomerFundingAccess */]: false,
  ["CustomerServiceAgreementsAccess" /* CustomerServiceAgreementsAccess */]: false,
  ["CustomerBudgetsAccess" /* CustomerBudgetsAccess */]: false
};
var ALL_CUSTOMER_TAB_ACTIONS_ENABLED = {
  ["CustomerContactsAccess" /* CustomerContactsAccess */]: true,
  ["CustomerFundingAccess" /* CustomerFundingAccess */]: true,
  ["CustomerServiceAgreementsAccess" /* CustomerServiceAgreementsAccess */]: true,
  ["CustomerBudgetsAccess" /* CustomerBudgetsAccess */]: true
};
function getDefaultFineGrainedPermissions(permissionRoles) {
  const hasViewCustomerProfileAccess = permissionRoles.some(
    (role) => ROLES_WITH_CUSTOMER_PROFILE_ACCESS.includes(role.permissionRole)
  );
  if (hasViewCustomerProfileAccess) return ALL_CUSTOMER_TAB_ACTIONS_ENABLED;
  return DEFAULT_FINE_GRAINED_ACTIONS;
}

// src/permissions/validation.ts
var PermissionValidator = class _PermissionValidator {
  /**
   * Get permission information for a given action
   */
  static getPermissionInfo(permission) {
    return userPermission[permission];
  }
  /**
   * Validate user permissions for a given action
   */
  static validateBackendPermission({
    permission,
    userPermissions,
    serviceId,
    departmentId,
    serviceIds,
    departmentIds,
    fineGrainedAction,
    requireFineGrainedAction
  }) {
    const info = _PermissionValidator.getPermissionInfo(permission);
    let isNewUser = false;
    for (let i = 0; i < userPermissions.permissionRoles.length; i++) {
      const userPermissionRole = userPermissions.permissionRoles[i];
      if (userPermissionRole && (userPermissionRole.permissionRole === 13 /* WorkflowBasic */ || userPermissionRole.permissionRole === 14 /* WorkflowElevated */ || userPermissionRole.permissionRole === 15 /* WorkflowManager */)) {
        isNewUser = true;
        break;
      }
    }
    const workingPermissionRoles = [...userPermissions.permissionRoles];
    if (!isNewUser) {
      workingPermissionRoles.push({
        permissionRole: 13 /* WorkflowBasic */,
        serviceDepartmentId: null,
        serviceId: null
      });
    }
    if (requireFineGrainedAction && fineGrainedAction) {
      const hasFineGrainedAccess = _PermissionValidator.validateFineGrainedAction(fineGrainedAction, userPermissions);
      if (!hasFineGrainedAccess) {
        return false;
      }
    }
    if (info.requireValidateServiceAccess) {
      if (departmentId) {
        return _PermissionValidator.validateSingleService(info, workingPermissionRoles, serviceId, departmentId);
      }
      return _PermissionValidator.validateMultipleServices(info, workingPermissionRoles, serviceIds, departmentIds);
    }
    let requiredPermissionRole = info.permissionRoles;
    if (info.childPermissionRoles) {
      let hasPrimaryOwner = false;
      for (let i = 0; i < requiredPermissionRole.length; i++) {
        if (requiredPermissionRole[i] === 1 /* PrimaryOwner */) {
          hasPrimaryOwner = true;
          break;
        }
      }
      if (hasPrimaryOwner) return true;
      let userPermissionRole = null;
      for (let i = 0; i < info.permissionRoles.length; i++) {
        const role = info.permissionRoles[i];
        for (let j = 0; j < workingPermissionRoles.length; j++) {
          if (workingPermissionRoles[j]?.permissionRole === role) {
            userPermissionRole = role;
            break;
          }
        }
        if (userPermissionRole) break;
      }
      if (!userPermissionRole) return false;
      const childRoles = info.childPermissionRoles?.[userPermissionRole.toString()];
      if (!childRoles) return false;
      requiredPermissionRole = childRoles;
    }
    const matchedUserPermission = [];
    for (let i = 0; i < workingPermissionRoles.length; i++) {
      const userPermissionRole = workingPermissionRoles[i];
      if (!userPermissionRole) continue;
      let hasMatch = false;
      for (let j = 0; j < requiredPermissionRole.length; j++) {
        if (requiredPermissionRole[j] === userPermissionRole.permissionRole) {
          hasMatch = true;
          break;
        }
      }
      if (hasMatch) {
        matchedUserPermission.push(userPermissionRole);
      }
    }
    return matchedUserPermission.length > 0;
  }
  static validateMultipleServices(info, userPermissionRoles, serviceIds, departmentIds) {
    if (!serviceIds || !departmentIds) return false;
    if (serviceIds.length !== departmentIds.length) {
      throw new Error("serviceIds and departmentIds do not match");
    }
    for (let i = 0; i < departmentIds.length; i++) {
      if (!_PermissionValidator.validateSingleService(info, userPermissionRoles, serviceIds[i], departmentIds[i])) {
        return false;
      }
    }
    return true;
  }
  static validateSingleService(info, userPermissionRoles, serviceId, departmentId) {
    if ((serviceId !== void 0 || serviceId !== null) && departmentId) {
      let matchedUserPermission = null;
      for (let i = 0; i < userPermissionRoles.length; i++) {
        const userPermissionRole = userPermissionRoles[i];
        if (!userPermissionRole) continue;
        let permissionMatch = false;
        for (let j = 0; j < info.permissionRoles.length; j++) {
          if (info.permissionRoles[j] === userPermissionRole.permissionRole) {
            permissionMatch = true;
            break;
          }
        }
        if (permissionMatch && userPermissionRole) {
          if ([1 /* PrimaryOwner */, 2 /* Owner */].includes(userPermissionRole.permissionRole)) {
            matchedUserPermission = userPermissionRole;
            break;
          }
          if (userPermissionRole.serviceDepartmentId === departmentId && userPermissionRole.serviceId === serviceId || userPermissionRole.serviceDepartmentId === departmentId && userPermissionRole.serviceId === null) {
            matchedUserPermission = userPermissionRole;
            break;
          }
        }
      }
      return Boolean(matchedUserPermission);
    }
    throw new Error("serviceId and serviceDepartmentID not set");
  }
  /**
   * Frontend validation method that matches the old workspace implementation
   */
  static validateFrontendPermission = (permissionKey, userPermissionRoles, serviceDepartmentId, serviceId) => {
    const permission = userPermission[permissionKey];
    if (!permission) {
      console.warn(`Permission "${permissionKey}" not found in userPermission object`);
      return false;
    }
    if (permission.requireValidateServiceAccess) {
      const matchedUserPermissionRole2 = userPermissionRoles.find((userPermissionRole) => {
        const matchedPermission = permission.permissionRoles.findIndex(
          (permissionRole) => [1 /* PrimaryOwner */, 2 /* Owner */].includes(permissionRole) && permissionRole === userPermissionRole.permissionRole
        ) >= 0;
        return matchedPermission ? matchedPermission : permission.permissionRoles.findIndex((permissionRole) => {
          return (userPermissionRole.serviceDepartmentId === serviceDepartmentId && userPermissionRole.serviceId === serviceId || userPermissionRole.serviceDepartmentId === serviceDepartmentId && userPermissionRole.serviceId === null) && permissionRole === userPermissionRole.permissionRole;
        }) >= 0;
      });
      return !!matchedUserPermissionRole2;
    }
    const matchedUserPermissionRole = userPermissionRoles.find((userPermissionRole) => {
      return permission.permissionRoles.findIndex(
        (permissionRole) => permissionRole === userPermissionRole.permissionRole
      ) >= 0;
    });
    return !!matchedUserPermissionRole;
  };
  /**
   * Validate fine-grained action permissions
   */
  static validateFineGrainedAction(action, userPermissions, context) {
    if (_PermissionValidator.evaluatePermission("BypassFineGrainedActions", userPermissions, {
      ...context,
      backendPermissionCheck: context?.backendPermissionCheck ?? false
    })) {
      return true;
    }
    if (!userPermissions.fineGrainedActions) {
      const defaultPermissions2 = getDefaultFineGrainedPermissions(userPermissions.permissionRoles);
      return defaultPermissions2[action] === true;
    }
    return userPermissions.fineGrainedActions[action] === true;
  }
  /**
   * Validates an enhanced permission rule against user permissions
   */
  static validateEnhancedBackendPermission(rule, userPermissions, context) {
    return _PermissionValidator.evaluateRule(rule, userPermissions, { ...context, backendPermissionCheck: true });
  }
  static validateEnhancedFrontendPermission(rule, userPermissions, context) {
    return _PermissionValidator.evaluateRule(rule, userPermissions, { ...context, backendPermissionCheck: false });
  }
  /**
   * Recursively evaluates permission rules with AND/OR/NOT logic
   */
  static evaluateRule(rule, userPermissions, context) {
    if (rule.AND)
      return rule.AND.every((subRule) => _PermissionValidator.evaluateRule(subRule, userPermissions, context));
    if (rule.OR) return rule.OR.some((subRule) => _PermissionValidator.evaluateRule(subRule, userPermissions, context));
    if (rule.NOT) return !_PermissionValidator.evaluateRule(rule.NOT, userPermissions, context);
    if (rule.permission) return _PermissionValidator.evaluatePermission(rule.permission, userPermissions, context);
    if (rule.fineGrainedAction)
      return _PermissionValidator.validateFineGrainedAction(rule.fineGrainedAction, userPermissions, context);
    return false;
  }
  /**
   * Evaluates a standard permission using the existing validation system
   */
  static evaluatePermission(permission, userPermissions, context) {
    if (context.backendPermissionCheck) {
      return _PermissionValidator.validateBackendPermission({
        ...context,
        permission,
        userPermissions
      });
    }
    return _PermissionValidator.validateFrontendPermission(
      permission,
      userPermissions.permissionRoles,
      context?.departmentId,
      context?.serviceId ?? void 0
    );
  }
  /**
   * Convenience method for checking a single fine-grained action
   */
  static hasFineGrainedAction(action, userPermissions) {
    return _PermissionValidator.validateFineGrainedAction(action, userPermissions);
  }
  /**
   * Check if user has access to customer profile tab
   */
  static hasCustomerTabAccess(tabType, userPermissions, context) {
    return _PermissionValidator.validateEnhancedFrontendPermission(
      {
        AND: [{ permission: "ViewCustomerProfile" }, { fineGrainedAction: tabType }]
      },
      userPermissions,
      context
    );
  }
  /**
   * Check if user has access to manage custom role templates.
   * Restricted to Primary Owners and Owners only.
   */
  static hasCustomRoleTemplateAccess(userPermissions) {
    return userPermissions.permissionRoles.some(
      (r) => r.permissionRole === 1 /* PrimaryOwner */ || r.permissionRole === 2 /* Owner */
    );
  }
};
var PermissionRoleSchema = z.object({
  permissionRole: z.nativeEnum(UserPermissionRole),
  serviceDepartmentId: z.string().uuid().nullable(),
  serviceId: z.string().uuid().nullable()
});
var UserPermissionModelSchema = z.object({
  hasWorkerAppAccess: z.boolean(),
  hasPortalAccess: z.boolean(),
  permissionRoles: z.array(PermissionRoleSchema),
  fineGrainedActions: z.record(z.nativeEnum(FineGrainedAction), z.boolean()).optional()
});
var CustomRoleTemplateStatusSchema = z.enum(["ACTIVE", "ARCHIVED"]);
var CustomRoleTemplateSchema = z.object({
  customRoleTemplateId: z.string().uuid(),
  serviceProviderId: z.string().uuid(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).nullable(),
  permissions: UserPermissionModelSchema,
  status: CustomRoleTemplateStatusSchema,
  createdBy: z.string().uuid(),
  createdOn: z.date(),
  updatedBy: z.string().uuid().nullable(),
  updatedOn: z.date(),
  archivedOn: z.date().nullable()
});
var CustomRoleTemplateListItemSchema = z.object({
  customRoleTemplateId: z.string().uuid(),
  serviceProviderId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  permissions: UserPermissionModelSchema,
  status: CustomRoleTemplateStatusSchema,
  createdBy: z.string().uuid(),
  createdOn: z.date(),
  updatedBy: z.string().uuid().nullable(),
  updatedOn: z.date(),
  archivedOn: z.date().nullable(),
  assignedWorkerCount: z.number().int().min(0).optional(),
  assignedWorkerIds: z.array(z.string().uuid()).optional()
});
var CreateCustomRoleTemplateInputSchema = z.object({
  name: z.string().min(1, "Template name is required").max(100, "Template name must be 100 characters or less"),
  description: z.string().max(500, "Description must be 500 characters or less").optional().transform((val) => val || null),
  permissions: UserPermissionModelSchema
});
var UpdateCustomRoleTemplateInputSchema = z.object({
  customRoleTemplateId: z.string().uuid(),
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).nullable().optional().transform((val) => val === void 0 ? void 0 : val || null),
  permissions: UserPermissionModelSchema.optional()
});
var ArchiveCustomRoleTemplateInputSchema = z.object({
  customRoleTemplateId: z.string().uuid()
});
var BulkAssignTemplateInputSchema = z.object({
  customRoleTemplateId: z.string().uuid(),
  supportWorkerIds: z.array(z.string().uuid()).min(1, "At least one worker must be specified").max(100, "Cannot assign more than 100 workers at once"),
  clearIndividualPermissions: z.boolean().default(false)
});
var BulkUnassignTemplateInputSchema = z.object({
  supportWorkerIds: z.array(z.string().uuid()).min(1, "At least one worker must be specified").max(100, "Cannot unassign more than 100 workers at once")
});
var BulkAssignmentResultSchema = z.object({
  successful: z.array(z.string().uuid()),
  failed: z.array(
    z.object({
      supportWorkerId: z.string().uuid(),
      reason: z.string()
    })
  ),
  totalProcessed: z.number().int().min(0),
  successCount: z.number().int().min(0),
  failureCount: z.number().int().min(0)
});
var PermissionSourceSchema = z.enum(["template", "individual", "both", "none"]);
var AuditedPermissionRoleSchema = z.object({
  permissionRole: z.nativeEnum(UserPermissionRole),
  permissionRoleName: z.string(),
  serviceDepartmentId: z.string().uuid().nullable(),
  serviceDepartmentName: z.string().nullable(),
  serviceId: z.string().uuid().nullable(),
  serviceName: z.string().nullable(),
  source: PermissionSourceSchema
});
var AuditedFineGrainedActionSchema = z.object({
  action: z.nativeEnum(FineGrainedAction),
  actionName: z.string(),
  granted: z.boolean(),
  source: PermissionSourceSchema,
  templateValue: z.boolean().nullable(),
  individualValue: z.boolean().nullable()
});
var PermissionAuditTemplateReferenceSchema = z.object({
  customRoleTemplateId: z.string().uuid(),
  name: z.string(),
  status: CustomRoleTemplateStatusSchema
}).nullable();
var PermissionAuditResultSchema = z.object({
  supportWorkerId: z.string().uuid(),
  supportWorkerName: z.string(),
  template: PermissionAuditTemplateReferenceSchema,
  effectivePermissions: z.object({
    hasPortalAccess: z.boolean(),
    hasPortalAccessSource: PermissionSourceSchema,
    hasWorkerAppAccess: z.boolean(),
    hasWorkerAppAccessSource: PermissionSourceSchema,
    permissionRoles: z.array(AuditedPermissionRoleSchema),
    fineGrainedActions: z.array(AuditedFineGrainedActionSchema)
  })
});
var WorkerTemplateAssignmentSchema = z.object({
  supportWorkerId: z.string().uuid(),
  supportWorkerName: z.string(),
  customRoleTemplateId: z.string().uuid().nullable(),
  customRoleTemplateName: z.string().nullable(),
  assignedOn: z.date().nullable()
});
var AssignTemplateToWorkerInputSchema = z.object({
  supportWorkerId: z.string().uuid(),
  customRoleTemplateId: z.string().uuid()
});
var UnassignTemplateFromWorkerInputSchema = z.object({
  supportWorkerId: z.string().uuid()
});

export { ALL_CUSTOMER_TAB_ACTIONS_ENABLED, ArchiveCustomRoleTemplateInputSchema, AssignTemplateToWorkerInputSchema, AuditedFineGrainedActionSchema, AuditedPermissionRoleSchema, BulkAssignTemplateInputSchema, BulkAssignmentResultSchema, BulkUnassignTemplateInputSchema, CreateCustomRoleTemplateInputSchema, CustomRoleTemplateListItemSchema, CustomRoleTemplateSchema, CustomRoleTemplateStatusSchema, DEFAULT_FINE_GRAINED_ACTIONS, FineGrainedAction, PermissionAuditResultSchema, PermissionAuditTemplateReferenceSchema, PermissionRoleSchema, PermissionSourceSchema, PermissionValidator, ROLES_WITH_CUSTOMER_PROFILE_ACCESS, SILServiceDetailsResponse, UnassignTemplateFromWorkerInputSchema, UpdateCustomRoleTemplateInputSchema, UserPermissionModelSchema, UserPermissionRole, WorkerTemplateAssignmentSchema, addressSchema, addressUpdateSchema, categoryType, customer, defaultPermissions, getDefaultFineGrainedPermissions, notification, serviceAttachmentsUpdateSchema, serviceLocationUpdateSchema, serviceUpdateSchema, userPermission };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map